import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthenticationService } from '@app/auth';
import { I18nService } from '@app/i18n';
import { ButtonPadding, ButtonVariant } from '@app/components/button/button.component';
import { PlInputType } from '@app/components/pl-input/pl-input.component';
import { firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { PlRouter } from '@shared/services/pl-router.service';
import { getRoutePath, PlRoute } from '@app/utils/routes';
import { QueryParamsForgotPassword } from '@app/auth/forgot-password/forgot-password.component';

enum FormControlName {
  password = 'password',
  password2 = 'password2',
}

interface PasswordSetupForm {
  [FormControlName.password]: FormControl<string | null>;
  [FormControlName.password2]: FormControl<string | null>;
}
interface PasswordSetupFormValue {
  [FormControlName.password]: string | null;
  [FormControlName.password2]: string | null;
}

export interface PasswordSetupRouteData {
  title?: string;
  createPassword: boolean;
}

@Component({
  selector: 'pl-password-setup',
  templateUrl: './password-setup.component.html',
})
export class PasswordSetupComponent implements OnInit {
  public error: string | null = null;
  public passwordSetupForm!: FormGroup<PasswordSetupForm>;
  public isLoading: boolean = false;

  public isCreate: boolean = false;
  public showAfterCreateInfo: boolean = false;

  protected readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  protected readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  protected readonly FormControlName: typeof FormControlName = FormControlName;
  protected readonly PlInputType: typeof PlInputType = PlInputType;

  private destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private authenticationService: AuthenticationService,
    private i18nService: I18nService,
    private route: ActivatedRoute,
    private plRouter: PlRouter,
  ) {
    this.createForm();
  }

  public async ngOnInit(): Promise<void> {
    const routeData: PasswordSetupRouteData = (await firstValueFrom(this.route.data)) as PasswordSetupRouteData;
    this.isCreate = routeData.createPassword;

    this.watchFormForCheckingSamePassword();
  }

  public setupPassword(): void {
    if (!this.passwordSetupForm.valid) {
      this.passwordSetupForm.controls.password.markAsDirty();
      this.passwordSetupForm.controls.password2.markAsDirty();
    } else {
      this.isLoading = true;

      const token: string = this.route.snapshot.queryParams['token'];
      if (!token) {
        const queryParamForgotPassword: QueryParamsForgotPassword = {
          errorMessage: this.i18nService.translate('login.errors.reset_password_token_expired'),
        };
        this.plRouter.navigate([getRoutePath(PlRoute.retrieveCredentials)], {
          queryParams: queryParamForgotPassword,
        });
      } else {
        const password: string = this.passwordSetupForm.controls.password.value as string;
        this.authenticationService
          .setupPassword(this.isCreate, password, token)
          .pipe(
            finalize((): void => {
              this.passwordSetupForm.markAsPristine();
              this.isLoading = false;
            }),
          )
          .subscribe({
            next: (): void => {
              if (this.isCreate) {
                this.showAfterCreateInfo = true;
              } else {
                this.plRouter.navigate([getRoutePath(PlRoute.home)]);
              }
            },
            error: (error: HttpErrorResponse): void => {
              console.error('There was an error resetting the password, error=', error);
              if (error.status === 401 || error.status === 404) {
                const queryParamForgotPassword: QueryParamsForgotPassword = {
                  errorMessage: this.i18nService.translate('login.errors.reset_password_token_expired'),
                };
                this.plRouter.navigate([getRoutePath(PlRoute.retrieveCredentials)], {
                  queryParams: queryParamForgotPassword,
                });
              } else {
                this.error = this.i18nService.translate(`login.errors.unexpected_error`);
              }
            },
          });
      }
    }
  }

  private createForm(): void {
    const fb: FormBuilder = new FormBuilder();
    this.passwordSetupForm = fb.nonNullable.group<PasswordSetupForm>({
      password: fb.control<string>('', [Validators.required, Validators.pattern(/^[^\s]{8,}$/)]),
      password2: fb.control<string>('', [Validators.required, Validators.pattern(/^[^\s]{8,}$/)]),
    });
  }

  public watchFormForCheckingSamePassword(): void {
    this.passwordSetupForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((form: Partial<PasswordSetupFormValue>): void => {
        const password: string = form.password as string;
        const password2: string = form.password2 as string;
        if (password !== password2) {
          this.error = this.i18nService.translate('login.errors.password_no_match');
        } else {
          this.error = null;
        }
      });
  }

  public showLoginScreen(): void {
    this.plRouter.navigate([getRoutePath(PlRoute.login)]);
  }
}
